import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePoll } from '../contexts/PollContext';
import { usePlayer } from '../contexts/PlayerContext';
import PollLobby from './PollLobby';
import PollRoom from './PollRoom';

function PollApp() {
  const { roomId: urlRoomId } = useParams<{ roomId: string }>();
  const { currentPoll, joinPollRoom, connected } = usePoll();
  const { player, isAuthenticated } = usePlayer();
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Track if we've already joined this room to prevent reconnection loops
  const joinedRoomRef = useRef<string | null>(null);

  // Auto-join poll room when component mounts (only once per room)
  useEffect(() => {
    if (urlRoomId && player?.id && player?.nickname && connected && joinedRoomRef.current !== urlRoomId) {
      console.log('🚪 PollApp: Joining poll room:', urlRoomId, 'connected:', connected);
      joinPollRoom(urlRoomId, player.nickname, player.id);
      joinedRoomRef.current = urlRoomId;
    } else if (urlRoomId && player?.id && player?.nickname && !connected) {
      console.log('⏳ PollApp: Waiting for socket connection before joining room:', urlRoomId);
    }
  }, [urlRoomId, player?.id, player?.nickname, connected]);

  // Debug logging
  console.log('🔍 PollApp state:', {
    urlRoomId,
    connected,
    currentPoll: !!currentPoll,
    pollState: currentPoll?.state,
    pollQuestion: currentPoll?.question,
    playerId: player?.id,
    playerNickname: player?.nickname,
    joinedRoom: joinedRoomRef.current
  });

  // Show poll room if there's an active poll OR if we're viewing a specific poll by ID (past polls)
  // PollRoom handles both active polls (via socket) and past polls (via fetch)
  if (currentPoll || urlRoomId) {
    console.log('📊 PollApp: Showing PollRoom for', currentPoll ? 'active poll: ' + currentPoll.question : 'poll ID: ' + urlRoomId);
    return <PollRoom />;
  }

  console.log('📊 PollApp: Showing PollLobby (no currentPoll, no roomId)');
  return <PollLobby />;
}

export default PollApp;
